<script setup lang="ts">
import embedBanner from "@/assets/img/integrate-form-banner.png";
import { useEmitter } from "@/composition/useEmitter";
import { useProductNoun } from "@/composition/useProductNoun";
import { useAppStore } from "@/store/app";
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import GhostFormPreview from "./GhostFormPreview.vue";

type PreviewLayout = "sidebar" | "popover" | "popup" | "standard" | "share";
const props = defineProps<{
  layout: PreviewLayout;
  formWidth: number;
  formHeight?: number;
}>();
const store = useAppStore();
const { t } = useI18n();
const { assetParams } = useProductNoun();
const emitter = useEmitter();
const previewRoot = shallowRef<HTMLElement | null>(null);
const frameSize = ref({ width: 0, height: 0 });
const DESKTOP_REFERENCE_VIEWPORT_WIDTH = 1440;
const IFRAME_MESSAGE_PREFIX = "[iFrameSizer]";
const iframeId = computed(() =>
  props.layout === "share"
    ? "integrate-share-preview-widget"
    : "integrate-embed-preview-widget"
);
const PREVIEW_TIMEOUT_MS = 10_000;
const previewWidths = {
  sidebar: { minimum: 160, gutter: 24 },
  popover: { minimum: 160, gutter: 48 },
  popup: { minimum: 160, gutter: 48 },
  standard: { minimum: 160, gutter: 48 },
  share: { minimum: 160, gutter: 64 },
} as const;
const boundedPixels = (value: number, minimum: number, available: number) =>
  `${Number(
    Math.min(Math.max(minimum, value), Math.max(0, available)).toFixed(2)
  )}px`;
const measurePreview = () => {
  const frame = previewRoot.value?.parentElement;
  if (!frame) return;
  const { width, height } = frame.getBoundingClientRect();
  if (
    width > 0 &&
    height > 0 &&
    (width !== frameSize.value.width || height !== frameSize.value.height)
  ) {
    frameSize.value = { width, height };
  }
};
let frameObserver: ResizeObserver | undefined;
const previewIframe = shallowRef<HTMLIFrameElement | null>(null);
const iframeVersion = ref(0);
const iframeLoaded = ref(false);
const reportedHeight = ref<number | null>(null);
const fallback = ref(false);
const previewReady = computed(
  () => iframeLoaded.value && reportedHeight.value !== null && !fallback.value
);
const widgetUrl = computed(() => {
  const url = new URL(store.integrateShareUrl, window.location.href);
  url.searchParams.set("notrack", "true");
  url.searchParams.set("nocache", "true");
  return url.href;
});
const widgetOrigin = computed(() => new URL(widgetUrl.value).origin);
let previewTimer: ReturnType<typeof setTimeout> | undefined;
const clearPreviewTimer = () => {
  if (previewTimer !== undefined) clearTimeout(previewTimer);
  previewTimer = undefined;
};
const startPreviewTimer = () => {
  clearPreviewTimer();
  previewTimer = setTimeout(() => {
    fallback.value = true;
  }, PREVIEW_TIMEOUT_MS);
};
const restartPreview = () => {
  previewIframe.value = null;
  iframeLoaded.value = false;
  reportedHeight.value = null;
  fallback.value = false;
  iframeVersion.value += 1;
  startPreviewTimer();
};
const onBuilderSaved = (payload: { widgetId: string }) => {
  if (payload.widgetId === store.integrateWidget.id) restartPreview();
};
const onHeightMessage = (event: MessageEvent) => {
  const iframe = previewIframe.value;
  if (
    !iframe ||
    !iframe.contentWindow ||
    event.source !== iframe.contentWindow ||
    event.origin !== widgetOrigin.value ||
    typeof event.data !== "string" ||
    !event.data.startsWith(IFRAME_MESSAGE_PREFIX)
  )
    return;
  const [id, rawHeight, , type] = event.data
    .slice(IFRAME_MESSAGE_PREFIX.length)
    .split(":");
  const height = Number(rawHeight);
  if (
    id !== iframeId.value ||
    !type ||
    [
      "close",
      "message",
      "scrollTo",
      "scrollToOffset",
      "inPageLink",
      "reset",
    ].includes(type) ||
    !Number.isFinite(height) ||
    height <= 0
  )
    return;
  reportedHeight.value = Math.ceil(height + 5);
  if (iframeLoaded.value) clearPreviewTimer();
};
const onIframeLoad = () => {
  const iframe = previewIframe.value;
  if (!iframe) return;
  iframeLoaded.value = true;
  // Match the iframe-resizer parent init payload used by form_embed.js.
  iframe.contentWindow?.postMessage(
    `${IFRAME_MESSAGE_PREFIX}${iframeId.value}:8:false:false:32:true:true:null:offset:null:null:0`,
    widgetOrigin.value
  );
  if (reportedHeight.value !== null) clearPreviewTimer();
};
const onIframeError = () => {
  fallback.value = true;
  clearPreviewTimer();
};
onMounted(() => {
  const frame = previewRoot.value?.parentElement;
  if (frame && typeof ResizeObserver !== "undefined") {
    frameObserver = new ResizeObserver(measurePreview);
    frameObserver.observe?.(frame);
  }
  measurePreview();
  window.addEventListener("message", onHeightMessage);
  emitter.on("builder:saved", onBuilderSaved);
  startPreviewTimer();
});
onUnmounted(() => {
  frameObserver?.disconnect?.();
  window.removeEventListener("message", onHeightMessage);
  emitter.off("builder:saved", onBuilderSaved);
  clearPreviewTimer();
});
watch(widgetUrl, restartPreview);
const formPreviewStyle = computed(() => {
  const sizing = previewWidths[props.layout];
  const scale = frameSize.value.width
    ? frameSize.value.width / DESKTOP_REFERENCE_VIEWPORT_WIDTH
    : 1;
  const isFloatingModal = ["popup", "popover"].includes(props.layout);
  const savedFormWidth = Number(store.formStyle.width);
  const visibleFormWidth =
    isFloatingModal && savedFormWidth > 0
      ? Math.min(props.formWidth, savedFormWidth)
      : props.formWidth;
  const modalInset = isFloatingModal ? 6 * scale : 0;
  const defaultHeight = props.formHeight ?? 500;
  const realHeight = reportedHeight.value ?? defaultHeight;
  const width = boundedPixels(
    visibleFormWidth * scale + modalInset * 2,
    sizing.minimum,
    frameSize.value.width
      ? frameSize.value.width - sizing.gutter
      : sizing.minimum
  );
  const verticalGutter =
    props.layout === "popover" ? 120 : props.layout === "popup" ? 48 : 0;
  const availableHeight = frameSize.value.height
    ? Math.max(0, frameSize.value.height - verticalGutter)
    : Infinity;
  const visibleHeight =
    props.layout === "standard" || props.layout === "share"
      ? realHeight * scale
      : Math.min((props.formHeight ?? realHeight) * scale, availableHeight);
  const height = `${Number(Math.max(0, visibleHeight).toFixed(2))}px`;
  return {
    "--preview-form-width": width,
    "--preview-form-height": height,
    "--preview-real-width": `${visibleFormWidth}px`,
    "--preview-iframe-height": `${Math.max(0, visibleHeight / scale)}px`,
    "--preview-scale": scale,
    "--preview-modal-inset": `${Number(modalInset.toFixed(2))}px`,
    "--preview-close-top": `${Number((3 * scale).toFixed(2))}px`,
    "--preview-close-size": `${Number((23 * scale).toFixed(2))}px`,
    "--preview-close-icon-size": `${Number((24 * scale).toFixed(2))}px`,
    "--preview-close-border": `${Number(scale.toFixed(2))}px`,
  };
});
</script>

<template>
  <div
    ref="previewRoot"
    :data-testid="layout === 'share' ? 'share-preview-form' : 'embed-preview-form'"
    :data-layout="layout"
    class="real-form-preview"
    :class="{
      'real-form-preview--share': layout === 'share',
      'real-form-preview--fallback': fallback,
      'embed-preview__form--fallback': fallback && layout !== 'share',
    }"
    :style="formPreviewStyle"
  >
    <slot name="controls" />
    <template v-if="fallback">
      <p
        :data-testid="layout === 'share' ? 'share-preview-fallback-note' : 'embed-preview-fallback-note'"
        class="real-form-preview__fallback-note"
      >
        {{ t("integrate.embed.realPreviewUnavailable") }}
      </p>
      <GhostFormPreview
        class="real-form-preview__ghost-content"
        :banner-src="embedBanner"
        :banner-container-test-id="layout === 'share' ? undefined : 'embed-ghost-image'"
        :banner-test-id="layout === 'share' ? 'share-preview-banner' : 'embed-ghost-banner'"
        :banner-variant="layout === 'share' ? 'share' : 'embed'"
        :compact="['sidebar', 'popover'].includes(layout)"
        :submit-alignment="layout === 'share' ? 'right' : 'full'"
      />
    </template>
    <template v-else>
      <div
        v-if="!previewReady"
        :data-testid="layout === 'share' ? 'share-preview-skeleton' : 'embed-preview-skeleton'"
        class="real-form-preview__skeleton"
        aria-hidden="true"
      >
        <span></span><span></span><span></span>
      </div>
      <div
        class="real-form-preview__scaled-content"
        :class="{ 'real-form-preview__scaled-content--loading': !previewReady }"
      >
        <iframe
          :key="iframeVersion"
          :id="iframeId"
          ref="previewIframe"
          :data-testid="layout === 'share' ? 'share-preview-iframe' : 'embed-preview-iframe'"
          :src="widgetUrl"
          :title="t('integrate.embed.realPreviewTitle', assetParams)"
          loading="lazy"
          sandbox="allow-scripts allow-forms allow-same-origin"
          scrolling="yes"
          @load="onIframeLoad"
          @error="onIframeError"
        ></iframe>
      </div>
    </template>
  </div>
</template>

<style scoped>
.real-form-preview__ghost-content {
  flex: 1;
}
.real-form-preview__scaled-content {
  position: absolute;
  top: 0;
  left: var(--preview-modal-inset);
  width: var(--preview-real-width);
  height: var(--preview-iframe-height);
  transform: scale(var(--preview-scale));
  transform-origin: top left;
}
.real-form-preview__scaled-content--loading {
  opacity: 0;
  pointer-events: none;
}
.real-form-preview__scaled-content iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}
.real-form-preview__skeleton {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 36px 24px;
  background: var(--base-white, #fff);
}
.real-form-preview__skeleton span {
  height: 20px;
  border-radius: 4px;
  background: var(--gray-100, #f2f4f7);
}
.real-form-preview__skeleton span:nth-child(2) {
  width: 70%;
}
.real-form-preview__skeleton span:nth-child(3) {
  height: 140px;
}
.real-form-preview__fallback-note {
  margin: 0;
  color: var(--gray-600, #475467);
  font-size: 12px;
}
.real-form-preview--share {
  position: relative;
  width: var(--preview-form-width);
  height: var(--preview-form-height);
  flex: 0 0 auto;
  overflow: hidden;
  background: var(--base-white, #fff);
}
.real-form-preview--share.real-form-preview--fallback {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
}
.real-form-preview--share.real-form-preview--fallback .real-form-preview__ghost-content {
  height: auto;
}
</style>
